import { ExternalLink } from "lucide-react";
import type { CustomerCatalogueOption } from "@shared-core/data/customer-api-types";
import { customerLabelOf } from "@shared-core/domain/customer-option-wording";

/** The id of a group's hint line, for the control's `aria-describedby`. */
export function optionGroupHintId(option: CustomerCatalogueOption): string {
  return `option-${option.key}-group-hint`;
}

/** Whether a group has a hint line to show: an explanation, or a "learn more" URL. */
export function hasOptionGroupHint(option: CustomerCatalogueOption): boolean {
  return Boolean(option.customer_hint || option.learn_more_url);
}

// The dictionary's explanation of an option group (what "Lamination" means), as the small muted line under the
// group's heading, with its "Learn more" link when the wording carries a full URL. A guide page's name is not shown
// until guide pages exist. Renders nothing for a group with no wording, so such a group looks exactly as before.
export function OptionGroupHint({
  option,
}: {
  option: CustomerCatalogueOption;
}) {
  if (!hasOptionGroupHint(option)) return null;
  return (
    <p id={optionGroupHintId(option)} className="text-xs text-muted-foreground">
      {option.customer_hint}
      {option.learn_more_url && (
        <>
          {option.customer_hint ? " " : null}
          <a
            href={option.learn_more_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground underline underline-offset-2"
          >
            Learn more{" "}
            <span className="sr-only">
              about {customerLabelOf(option)} (opens in a new tab)
            </span>
            <ExternalLink
              className="inline size-3 align-[-1px]"
              aria-hidden="true"
            />
          </a>
        </>
      )}
    </p>
  );
}
